import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useGlobalActions } from '../../atoms';

export const RegisterPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { userRegister } = useGlobalActions();
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [fujiyamaInviteUrl, setFujiyamaInviteUrl] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const refCode = searchParams.get('ref');
    const [fujiyamaReferralCode, setFujiyamaReferralCode] = useState('');

    useEffect(() => {
        if (refCode) {
            setReferralCode(refCode);
        }
    }, [refCode]);

    useEffect(() => {
        try {
            if (fujiyamaInviteUrl) {
                const url = new URL(fujiyamaInviteUrl);
                const startParam = url.searchParams.get('start');
                if (startParam) {
                    setFujiyamaReferralCode(startParam);
                    setError('');
                } else {
                    setError('Invite URL does not contain a fujiyama tap referral code.');
                    setFujiyamaReferralCode('');
                }
            } else {
                setFujiyamaReferralCode('');
            }
        } catch {
            setError('Invalid Fujitama Tap Referral URL');
            setFujiyamaReferralCode('');
        }
    }, [fujiyamaInviteUrl]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await userRegister({
                email,
                username,
                password,
                fujiyama_referral_code: fujiyamaReferralCode,
                referral_code: referralCode || undefined
            });
            navigate('/');
        } catch (err: unknown) {
            if (err instanceof Response) {
                const text = await err.text();
                try {
                    const data = JSON.parse(text);
                    setError(data.message);
                } catch {
                    setError(text);
                }
            } else {
                setError('Network error. Please try again later.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F7F8F8] flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
                <div className="flex justify-center mb-4">
                    <img src="/logo.webp" alt="Fujiyama Logo" className="h-16" />
                </div>
                <h1 className="text-2xl font-bold text-center text-gray-800 mb-8">
                    Account Registration
                </h1>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="example@example.com"
                        />
                    </div>

                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                            User Name
                        </label>
                        <input
                            id="username"
                            type="text"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="username"
                        />
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                            Password: Include uppercase, lowercase, number, and more than 8 characters.
                        </label>
                        <input
                            id="password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="••••••••"
                        />
                    </div>

                    <div>
                        <label htmlFor="fujiyamaInviteUrl" className="block text-sm font-medium text-gray-700 mb-1">
                            Fujiyama Tap Invite URL
                        </label>
                        <input
                            id="fujiyamaInviteUrl"
                            type="url"
                            required
                            value={fujiyamaInviteUrl}
                            onChange={(e) => setFujiyamaInviteUrl(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="https://t.me/mountfuji4devstgbot?start=XXXXX"
                        />
                        {fujiyamaReferralCode && (
                            <p className="mt-1 text-sm text-green-600">
                                Fujiyama Referral Code: {fujiyamaReferralCode}
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1">
                            Referral Code{refCode ? '' : ' *Optional'}
                        </label>
                        <input
                            id="referralCode"
                            type="text"
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value)}
                            disabled={!!refCode}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${refCode ? 'bg-gray-100' : ''
                                }`}
                            placeholder="Referral Code"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 px-4 text-white rounded-md ${isLoading
                            ? 'bg-blue-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                            } transition-colors duration-200`}
                    >
                        {isLoading ? 'Loading...' : 'Register'}
                    </button>
                </form>
                <div className="mt-4 text-center text-sm">
                    <span className="text-gray-600">Already have an account?</span>{' '}
                    <Link to="/login" className="text-blue-600 hover:text-blue-700">
                        Login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;